import type {
    CSSProperties
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc
} from "./shared";
import {
    NCoreUIKitTheme,
    Badge,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    InlineCode,
    Paragraph,
    DocsTable,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "WebScrollbar";

const USAGE_CODE = `const NCoreUIKitBase = setupNCoreUIKit(config);

<NCoreUIKitBase.Provider>
    <App/>
</NCoreUIKitBase.Provider>`;

const LIST_ITEMS = [
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12
] as const;

const getListStyle = ({
    shapeConfigs
}: DocsStyleTokens): CSSProperties => ({
    height: shapeConfigs.chartHeight.small,
    overflow: "auto",
    width: "100%"
});

const getRowStyle = ({
    borders,
    colors,
    spaces
}: DocsStyleTokens): CSSProperties => ({
    borderBottom: `${borders.separator}px solid ${colors.content.border.subtle}`,
    padding: `${spaces.spacingSm}px ${spaces.spacingMd}px`
});

const getStripStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    paddingBottom: spaces.spacingMd,
    gap: spaces.spacingSm,
    overflowX: "auto",
    display: "flex",
    width: "100%"
});

const CHIP_STYLE: CSSProperties = {
    flexShrink: 0
};

const getTokensStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    alignItems: "flex-start",
    flexDirection: "column",
    gap: spaces.spacingXs,
    display: "flex"
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("webScrollbarPage.themingTitle"),
        id: "theming"
    },
    {
        title: t("webScrollbarPage.scopeTitle"),
        id: "scope"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const WebScrollbarPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        borders
    } = NCoreUIKitTheme.useContext();

    const getThickness = (border: number) => `${shapeConfigs.scrollBarWidth - border * 2} px`;

    const renderTokens = (tokens: Array<string>) => <div
        style={getTokensStyle(docsTokens)}
    >
        {
            tokens.map((token) => <InlineCode
                key={token}
            >
                {token}
            </InlineCode>)
        }
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("webScrollbarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("webScrollbarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("webScrollbarPage.overviewCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <div
                        style={getListStyle(docsTokens)}
                    >
                        {
                            LIST_ITEMS.map((item) => <div
                                style={getRowStyle(docsTokens)}
                                key={item}
                            >
                                <Text
                                    variant="bodyMediumSize"
                                    color="high"
                                >
                                    {t("webScrollbarPage.item", [
                                        item
                                    ])}
                                </Text>
                            </div>)
                        }
                    </div>
                </DemoFrame>
                <div
                    style={getStripStyle(docsTokens)}
                >
                    {
                        LIST_ITEMS.map((item) => <div
                            style={CHIP_STYLE}
                            key={item}
                        >
                            <Badge
                                title={t("webScrollbarPage.chip", [
                                    item
                                ])}
                                variant="outline"
                                type="neutral"
                                size="small"
                            />
                        </div>)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("webScrollbarPage.themingTitle")}
            id="theming"
        >
            <Paragraph>
                {t("webScrollbarPage.themingText")}
            </Paragraph>
            <DocsTable
                rows={[
                    [
                        t("webScrollbarPage.partBar"),
                        renderTokens([
                            "shapeConfigs.scrollBarWidth",
                            "shapeConfigs.scrollBarHeight"
                        ]),
                        `${shapeConfigs.scrollBarWidth} × ${shapeConfigs.scrollBarHeight} px`
                    ],
                    [
                        t("webScrollbarPage.partTrack"),
                        renderTokens([
                            "colors.content.container.subtle",
                            "borders.scrollBarTrack",
                            "radiuses.scrollBarTrack"
                        ]),
                        getThickness(borders.scrollBarTrack)
                    ],
                    [
                        t("webScrollbarPage.partThumb"),
                        renderTokens([
                            "colors.content.border.default",
                            "borders.scrollBarThumb",
                            "radiuses.scrollBarThumb"
                        ]),
                        getThickness(borders.scrollBarThumb)
                    ],
                    [
                        t("webScrollbarPage.partThumbHover"),
                        renderTokens([
                            "colors.content.border.hover",
                            "borders.scrollBarThumbHover",
                            "radiuses.scrollBarThumbHover"
                        ]),
                        getThickness(borders.scrollBarThumbHover)
                    ],
                    [
                        t("webScrollbarPage.partThumbActive"),
                        renderTokens([
                            "colors.content.text.mid",
                            "borders.scrollBarThumbActive",
                            "radiuses.scrollBarThumbActive"
                        ]),
                        getThickness(borders.scrollBarThumbActive)
                    ]
                ]}
                headers={[
                    t("webScrollbarPage.tablePart"),
                    t("webScrollbarPage.tableTokens"),
                    t("webScrollbarPage.tableSize")
                ]}
            />
        </Section>
        <Section
            title={t("webScrollbarPage.scopeTitle")}
            id="scope"
        >
            <BulletList
                items={[
                    t("webScrollbarPage.scopeWeb"),
                    t("webScrollbarPage.scopePointer"),
                    t("webScrollbarPage.scopeAll"),
                    t("webScrollbarPage.scopeEngine"),
                    t("webScrollbarPage.scopeStyle")
                ]}
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("webScrollbarPage.a11yVisual"),
                    t("webScrollbarPage.a11yBehaviour"),
                    t("webScrollbarPage.a11yTarget")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph
                isMuted={true}
            >
                {t("webScrollbarPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default WebScrollbarPage;
